import {
  hasMarketingConsent,
  identifyVisitor,
  trackMappedAnalyticsEvent,
} from "./visitorTracking";

type AnalyticsProperties = Record<string, unknown>;

type AnalyticsClient =
  | {
      capture?: (eventName: string, properties?: AnalyticsProperties) => void;
      identify?: (
        distinctId: string,
        properties?: Record<string, unknown>,
      ) => void;
    }
  | null
  | undefined;

type KlaviyoIdentity = {
  email?: string;
  phone_number?: string;
  [key: string]: unknown;
};

type KlaviyoCommandQueue = Array<
  | ["identify", KlaviyoIdentity, (() => void)?]
  | ["track", string, AnalyticsProperties?]
> & {
  identify: (properties: KlaviyoIdentity, callback?: () => void) => void;
  track: (eventName: string, properties?: AnalyticsProperties) => void;
};

type KlaviyoClient =
  | KlaviyoCommandQueue
  | {
      identify: (properties: KlaviyoIdentity, callback?: () => void) => void;
      track: (eventName: string, properties?: AnalyticsProperties) => void;
    };

declare global {
  interface Window {
    klaviyo?: KlaviyoClient;
  }
}

const KLAVIYO_SCRIPT_ID = "klaviyo-onsite-script";

let klaviyoScriptRequested = false;

const getKlaviyoCompanyId = () =>
  process.env.REACT_APP_KLAVIYO_PUBLIC_API_KEY?.trim() || "";

const ensureKlaviyoClient = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const existingClient = window.klaviyo;
  const queuedClient = (existingClient || []) as KlaviyoCommandQueue;

  if (typeof queuedClient.identify !== "function") {
    queuedClient.identify = (properties, callback) => {
      if (callback) {
        queuedClient.push(["identify", properties, callback]);
        return;
      }
      queuedClient.push(["identify", properties]);
    };
  }

  if (typeof queuedClient.track !== "function") {
    queuedClient.track = (eventName, properties) => {
      queuedClient.push(["track", eventName, properties]);
    };
  }

  window.klaviyo = queuedClient;
  return queuedClient;
};

const ensureKlaviyoScriptLoaded = () => {
  if (typeof document === "undefined") {
    return;
  }

  const companyId = getKlaviyoCompanyId();
  if (!companyId) {
    return;
  }

  if (document.getElementById(KLAVIYO_SCRIPT_ID)) {
    klaviyoScriptRequested = true;
    return;
  }

  if (klaviyoScriptRequested) {
    return;
  }

  klaviyoScriptRequested = true;

  const script = document.createElement("script");
  script.id = KLAVIYO_SCRIPT_ID;
  script.async = true;
  script.type = "text/javascript";
  script.src = `https://static.klaviyo.com/onsite/js/${encodeURIComponent(companyId)}/klaviyo.js`;
  script.onerror = () => {
    console.error("Failed to load Klaviyo onsite script");
    klaviyoScriptRequested = false;
  };
  document.body.appendChild(script);
};

const getKlaviyoClient = () => {
  if (!getKlaviyoCompanyId()) {
    console.warn(
      "Klaviyo company ID not set. Klaviyo analytics will be disabled.",
    );
    return null;
  }

  // Klaviyo is a marketing tool: only load/use it with marketing consent.
  if (!hasMarketingConsent()) {
    return null;
  }

  const client = ensureKlaviyoClient();
  ensureKlaviyoScriptLoaded();
  return client;
};

const trackKlaviyoEvent = (
  eventName: string,
  properties?: AnalyticsProperties,
) => {
  getKlaviyoClient()?.track(eventName, properties);
};

export const captureAnalyticsEvent = (
  client: AnalyticsClient,
  eventName: string,
  properties?: AnalyticsProperties,
) => {
  client?.capture?.(eventName, properties);
  trackKlaviyoEvent(eventName, properties);
  // Mirror key funnel actions into our first-party visitor stream (gated by
  // analytics consent inside the tracker).
  trackMappedAnalyticsEvent(eventName, properties);
};

export const identifyAnalyticsUser = (
  client: AnalyticsClient,
  identity: string | KlaviyoIdentity,
  callback?: () => void,
) => {
  const properties =
    typeof identity === "string" ? { email: identity } : identity;
  const distinctId = properties.email || properties.phone_number;

  if (!distinctId) {
    return;
  }

  client?.identify?.(distinctId);
  getKlaviyoClient()?.identify(properties, callback);
  // Link the anonymous visitor to their email in our first-party stream too.
  if (properties.email) identifyVisitor(String(properties.email));
};

export const capturePageView = (
  client: AnalyticsClient,
  properties: AnalyticsProperties,
) => {
  client?.capture?.("$pageview", properties);
  trackKlaviyoEvent("Viewed Page", properties);
};
